import { TypographyOptions } from '@mui/material/styles/createTypography';
import { lightModeColors } from '../themeColors';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    headerText: true;
    headerTextModal: true;
    headerTextSection: true;
    headerTextMessageModal: true;
    loginText: true;
    bodyTextPrimary: true;
    bodyTextPrimaryDark: true;
    helpText: true;
    greyText: true;
    bodyTableRowItem: true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    headerText: React.CSSProperties;
    headerTextModal: React.CSSProperties;
    headerTextSection: React.CSSProperties;
    headerTextMessageModal: React.CSSProperties;
    loginText: React.CSSProperties;
    bodyTextPrimary: React.CSSProperties;
    bodyTextPrimaryDark: React.CSSProperties;
    helpText: React.CSSProperties;
    greyText: React.CSSProperties;
    bodyTableRowItem: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    headerText?: React.CSSProperties;
    headerTextModal?: React.CSSProperties;
    headerTextSection?: React.CSSProperties;
    headerTextMessageModal?: React.CSSProperties;
    loginText?: React.CSSProperties;
    bodyTextPrimary?: React.CSSProperties;
    bodyTextPrimaryDark?: React.CSSProperties;
    helpText: React.CSSProperties;
    greyText: React.CSSProperties;
    bodyTableRowItem: React.CSSProperties;
  }
}

export const fontFamily =
  'Urbanist, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const typography: TypographyOptions = {
  fontFamily: [fontFamily].join(','),
  htmlFontSize: 16,
  fontSize: 13.333,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: {
    fontWeight: 800,
    fontSize: 64,
    fontFamily: fontFamily,
  },
  h2: {
    fontWeight: 800,
    fontFamily: fontFamily,
    fontSize: 55,
  },
  h3: {
    fontWeight: 800,
    fontSize: 40,
    letterSpacing: 0.4,
    fontFamily: fontFamily,
  },
  h4: {
    fontFamily: fontFamily,
    fontWeight: 800,
    fontSize: 32,
    letterSpacing: 0.32,
  },
  h5: {
    fontFamily: fontFamily,
    fontWeight: 800,
    fontSize: 24,
    letterSpacing: 0.24,
  },
  h6: {
    fontSize: 20,
    letterSpacing: 0.2,
    fontWeight: 800,
    fontFamily: fontFamily,
  },
  body1: {
    fontFamily: fontFamily,
    letterSpacing: 0.14,
    fontSize: 14,
  },
  body2: {
    fontFamily: fontFamily,
    letterSpacing: 0.12,
    fontSize: 12,
  },
  button: {
    fontWeight: 550,
    letterSpacing: 0.14,
  },
  caption: {
    fontSize: 13,
    textTransform: 'uppercase',
  },
  subtitle1: {
    fontSize: 15,
    fontWeight: 400,
  },
  subtitle2: {
    fontWeight: 300,
    fontSize: 14,
    fontStyle: 'italic',
  },
  overline: {
    fontSize: 14,
    fontWeight: 400,
    textTransform: 'uppercase',
  },
  // used for page header
  headerText: {
    fontWeight: 800,
    fontSize: '2.25rem',
    color: lightModeColors.primary.main || '#000000',
    fontFamily: fontFamily,
  },
  // used for header of pop up modals
  headerTextModal: {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: lightModeColors.primary.main || '#000000',
    fontFamily: fontFamily,
  },
  // used for header of sections in pop up modals
  headerTextSection: {
    fontWeight: 600,
    fontSize: '1rem',
    color: lightModeColors.primary.shade || '#000000',
    fontFamily: fontFamily,
  },
  // used for header of pop up messages
  headerTextMessageModal: {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: lightModeColors.primary.shade || '#000000',
    fontFamily: fontFamily,
  },
  bodyTextPrimary: {
    letterSpacing: 0.14,
    fontSize: 14,
    color: lightModeColors.primary.main || '#000000',
  },
  bodyTextPrimaryDark: {
    letterSpacing: 0.14,
    fontSize: 14,
    color: lightModeColors.primary.shade || '#000000',
  },
  bodyTableRowItem: {
    letterSpacing: 0.14,
    fontSize: 13,
    color: lightModeColors.primary.main || '#000000',
  },
  // used for help descriptions, e.g. use on paragraph below header explaining what to do on the page
  helpText: {
    fontFamily: fontFamily,
    letterSpacing: 0.14,
    fontSize: 14,
    fontStyle: 'italic',
    color: lightModeColors.primary.main || '#000000',
  },
  loginText: {
    fontWeight: 800,
    fontSize: '4.25rem',
    color: lightModeColors.primary.main || '#000000',
    fontFamily: fontFamily,
  },
  greyText: {
    color: lightModeColors.grey,
    fontFamily: fontFamily,
    letterSpacing: 0.14,
    fontSize: 14,
  },
  subHeaderText: {
    fontWeight: 600,
    fontSize: '1.125rem', // 18px
    color: lightModeColors.primary.main || '#000000',
    fontFamily: fontFamily,
  },
};
